// ============================================================
// myWorkLogic.ts
// Pure logic for the "My work" execution queue (no React, no store):
// urgency bucketing, priority ordering, due-date wording, and the date
// helpers shared by the page, the hero card, the tiles and the rails.
//
// URGENCY (from the task's due date, in the USER's local calendar):
//   overdue — not done, and the due moment has passed (timed tasks compare the
//             instant; all-day tasks — stored at midnight UTC — only once their
//             whole day is in the past). Same rule as the task modal's hero.
//   today   — due today (and not yet overdue).
//   week    — due later in the current Mon–Sun week.
//   later   — due after this week.
//   none    — no due date.
//   done    — status done (always its own collapsed group).
// Within a group: priority high → normal → low, then due date asc.
// ============================================================
import type { Task, TaskPriority } from '../../types/tasks.types'
import { normalisePriority } from '../shared/TaskRow'

export type Urgency = 'overdue' | 'today' | 'week' | 'later' | 'none' | 'done'
export const URGENCY_ORDER: Urgency[] = ['overdue', 'today', 'week', 'later', 'none', 'done']
export const PRIORITY_RANK: Record<TaskPriority, number> = { high: 0, normal: 1, low: 2 }

const DAY_MS = 86400000
const pad = (n: number): string => String(n).padStart(2, '0')
export const ymdLocal = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const startOfLocalDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())
// Local Monday 00:00 of the week containing d.
export const startOfLocalWeek = (d: Date): Date => {
  const s = startOfLocalDay(d)
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() - ((s.getDay() + 6) % 7))
}

// A dueDate is "timed" when it isn't midnight UTC (all-day tasks are stored at
// midnight UTC — the same rule the calendar grids use).
export const isTimedDue = (iso: string): boolean => {
  const d = new Date(iso)
  return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0
}
// The LOCAL calendar day a due date falls on. All-day → its plain Y-M-D (so a
// negative-offset timezone can't shift it); timed → the local day of the instant.
export const dueYmd = (iso: string): string => (isTimedDue(iso) ? ymdLocal(new Date(iso)) : iso.slice(0, 10))
const ymdToDate = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

// "Due soon" — THE rule for the dashboard Due soon tile's task count AND My
// work's "Due in 7 days" filter: not done, not overdue, due today through
// today + 7 days (local days; all-day → its stored day).
export const isTaskDueSoon = (t: Pick<Task, 'dueDate' | 'status'>, now: Date = new Date()): boolean => {
  if (!t.dueDate || t.status === 'done' || isTaskOverdue(t, now)) return false
  const end = ymdLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7))
  return dueYmd(t.dueDate) <= end
}

export const isTaskOverdue = (t: Pick<Task, 'dueDate' | 'status'>, now: Date = new Date()): boolean => {
  if (!t.dueDate || t.status === 'done') return false
  if (isTimedDue(t.dueDate)) return new Date(t.dueDate).getTime() < now.getTime()
  return t.dueDate.slice(0, 10) < ymdLocal(now)
}

export const urgencyOf = (t: Task, now: Date = new Date()): Urgency => {
  if (t.status === 'done') return 'done'
  if (!t.dueDate) return 'none'
  if (isTaskOverdue(t, now)) return 'overdue'
  const day = dueYmd(t.dueDate)
  const today = ymdLocal(now)
  if (day === today) return 'today'
  const weekEnd = new Date(startOfLocalWeek(now).getTime() + 7 * DAY_MS) // next Monday 00:00
  return ymdToDate(day).getTime() < weekEnd.getTime() ? 'week' : 'later'
}

// Queue order: urgency, then priority, then due date (nulls last), then title.
export const compareQueue = (a: Task, b: Task, now: Date = new Date()): number => {
  const ua = URGENCY_ORDER.indexOf(urgencyOf(a, now))
  const ub = URGENCY_ORDER.indexOf(urgencyOf(b, now))
  if (ua !== ub) return ua - ub
  const pa = PRIORITY_RANK[normalisePriority(a.priority)]
  const pb = PRIORITY_RANK[normalisePriority(b.priority)]
  if (pa !== pb) return pa - pb
  if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1
  if (a.dueDate && !b.dueDate) return -1
  if (!a.dueDate && b.dueDate) return 1
  return a.title.localeCompare(b.title)
}

// "Mon 28 Sep" — the short day label used in the due column + group headings.
// Built by hand: newer ICU renders en-GB 'short' September as "Sept", the
// mockup (and the rest of the app, cf. the dashboard's shortDayLabel) uses "Sep".
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const shortDay = (d: Date): string => `${WD[d.getDay()]} ${d.getDate()} ${MO[d.getMonth()]}`
const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })

// Relative distance ("in 2 days", "in 3 weeks") for future dates.
const relFuture = (days: number): string => {
  if (days <= 1) return 'tomorrow'
  if (days < 14) return `in ${days} days`
  if (days < 60) return `in ${Math.round(days / 7)} weeks`
  return `in ${Math.round(days / 30)} months`
}

// The two-line due cell: [primary, secondary].
export const dueLines = (t: Task, now: Date = new Date()): [string, string] => {
  if (t.status === 'done') {
    const at = t.completedAt ?? t.updatedAt
    return [`Done ${shortDay(new Date(at))}`, '']
  }
  if (!t.dueDate) return ['No due date', '']
  const u = urgencyOf(t, now)
  const day = ymdToDate(dueYmd(t.dueDate))
  const timed = isTimedDue(t.dueDate)
  const days = Math.round((day.getTime() - startOfLocalDay(now).getTime()) / DAY_MS)
  if (u === 'overdue') {
    if (days === 0) return [`Overdue · ${timeLabel(t.dueDate)}`, 'Today']
    const n = Math.abs(days)
    return [`Overdue by ${n} day${n === 1 ? '' : 's'}`, shortDay(day)]
  }
  if (u === 'today') return [timed ? `Today · ${timeLabel(t.dueDate)}` : 'Today', shortDay(day)]
  return [shortDay(day) + (timed ? ` · ${timeLabel(t.dueDate)}` : ''), relFuture(days)]
}

// Compact due chip for the hero ("Overdue by 4 days", "Today · 14:00", "Wed 30 Sep").
export const dueChip = (t: Task, now: Date = new Date()): string => dueLines(t, now)[0]

// Unclaimed firm-wide — mirrors the backend isUnclaimedFirmWide exactly.
export const isUnclaimed = (t: Task): boolean =>
  !!t.firmWide && (t.assignees ?? []).every((a) => a.userId === t.assignedToUserId)

// Is this user on the task (owner or co-assignee)? Firm-wide alone doesn't count.
export const isOnTask = (t: Task, userId: string): boolean =>
  t.assignedToUserId === userId || (t.assignees ?? []).some((a) => a.userId === userId)

// Owner first, then co-assignees (deduped) — the people chips order.
export const peopleOf = (t: Task): string[] => {
  const out = [t.assignedToUserId]
  for (const a of t.assignees ?? []) if (!out.includes(a.userId)) out.push(a.userId)
  return out
}
